import { StateCreator } from 'zustand';

import { SiteResultProps } from '@/modules/Auth/api/sites/sites.types.ts';
import { MapView } from '@/modules/Home/components/MapBody/MapBody.types.ts';
import {
  CurrencyEnum,
  DisplayTimeZoneEnum,
  SystemViewEnum,
  TimeFormatEnum,
} from '@/modules/Settings/types/system.types.ts';
import { UiSliceProps } from '@/shared/store/ui/uiSlice.types.ts';
import { LocaleEnum } from '@/shared/types/global/i18n.types.ts';

const defaultMapFilter = {
  actionEquipmentSubCategory: [],
  actionSafetySubCategory: [],
  actionSystemSubCategory: [],
  actionZoneSubCategory: [],
  assetItem: [],
  equipmentReservedBy: [],
  equipmentSubType: [],
  equipmentType: [],
  firm: [],
  group: [],
  resName: [],
  searchKey: '',
  tag: [],
  workerRole: [],
  zoneFloor: [],
  zoneItem: [],
  zoneType: [],
};

export const createUISlice: StateCreator<UiSliceProps> = (set, get) => ({
  accountName: '',

  accountPermissions: [],

  buttonsGroupView: 'time',

  clearTagsId: () => {
    set({ tagsId: [] });
  },
  confirmSaveSiteHours: false,

  currency: CurrencyEnum.USD,
  currentLanguage: LocaleEnum.EN,

  displayTimeZone: DisplayTimeZoneEnum.SITE,
  distance: 5,

  endTime: '',

  floor: null,
  floorsList: [],

  isCellCopyTipActive: true,

  isShowInactive: false,
  mapFilter: defaultMapFilter,
  mapGridVisible: false,
  mapView: MapView.FULL,

  permissionAccessLevels: {},
  permissionAccessLevelsLoading: true,

  readerVisibility: false,

  resetMapFilter: () => {
    set({ mapFilter: defaultMapFilter });
  },

  setAccountName: (accountName: string) => {
    set({ accountName });
  },

  setAccountPermissions: permissions => {
    set({ accountPermissions: permissions });
  },

  setButtonsGroupView: (buttonsGroupView: string) => {
    set({ buttonsGroupView });
  },

  setConfirmSaveSiteHours: value => {
    set({ confirmSaveSiteHours: value });
  },
  setCurrency: (currency: CurrencyEnum) => {
    set({ currency });
  },

  setCurrentLanguage: (location: LocaleEnum) => {
    set({ currentLanguage: location });
  },
  setDisplayTimeZone: value => {
    set({ displayTimeZone: value });
  },

  setDistance: distance => {
    set({ distance });
  },
  setEndTime: value => {
    set({ endTime: value });
  },

  setFloor: floor => {
    set({ floor });
  },

  setFloorsList: floorsList => {
    set({ floorsList });
  },

  setIsCellCopyTipActive: value => {
    set({ isCellCopyTipActive: value });
  },
  setIsShowInactive: (isShowInactive: boolean) => {
    set({ isShowInactive });
  },

  setMapFilter: filter => {
    const mapFilter = get().mapFilter;
    // Also deselect any geofences if a map filters change
    set({
      mapFilter: { ...mapFilter, ...filter },
      visibleGeofenceIds: [],
    } as never);
  },
  setMapGridVisible: visible => {
    set({ mapGridVisible: visible });
  },
  setMapView: view => {
    set({ mapView: view });
  },
  setPermissionAccessLevels: aclMap => {
    set({ permissionAccessLevels: aclMap, permissionAccessLevelsLoading: false });
  },
  setSite: (site: string) => {
    set({ site });
  },

  setSiteCalendars: siteCalendars => {
    set({ siteCalendars });
  },
  setSitePolicies: sitePolicies => {
    set({ sitePolicies });
  },

  setSiteShifts: siteShifts => {
    set({ siteShifts });
  },
  setSitesList: (sitesList: SiteResultProps[]) => {
    set({ sitesList, sitesListLoading: false });
  },
  setSitesListLoading: loading => {
    set({ sitesListLoading: loading });
  },

  setSiteTimeZone: value => {
    set({ siteTimeZone: value });
  },
  setStartTime: value => {
    set({ startTime: value });
  },

  setSystemView: (view: SystemViewEnum) => {
    set({ systemView: view });
  },
  setTagId: (tagId: string) => {
    set(state =>
      state.tagsId.includes(tagId)
        ? { tagsId: state.tagsId.filter(id => id !== tagId) }
        : { tagsId: [...state.tagsId, tagId] },
    );
  },

  setTagIds: dataOrSetter => {
    const currentTagIds = get().tagsId;
    set({ tagsId: Array.isArray(dataOrSetter) ? dataOrSetter : dataOrSetter(currentTagIds) });
  },

  setTimeFormat: value => {
    set({ timeFormat: value });
  },

  setUserSettings: settings => {
    set({ userSettings: settings });
  },

  site: '',
  siteCalendars: [],
  sitePolicies: [],
  siteShifts: [],

  sitesList: [],
  sitesListLoading: false,
  siteTimeZone: '',

  startTime: '',
  systemView: SystemViewEnum.METRIC,

  tagsId: [],
  timeFormat: TimeFormatEnum.H24,
  userSettings: [],
});
